import React, { cloneElement, useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs';
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from 'react-i18next';
import { X } from "lucide-react";
import { cn } from '@/lib/utils';
import { AgentCreditsIcon } from './assets/agent-credits-icon';
import { AgentCreditsImage } from './assets/agent-credits-image';
import { SlidingTabs } from '../common/sliding-tabs';
import { formatNumberWithCommas } from '@/utils/format-number-with-commas';
import { agentPermissionHooks, agentPermissionManager, subscriptionHook } from '@/manager';
import { AgentCreditsRecord } from '@/manager/manager/subscription-manager';

interface CreditsModalProps {
  trigger?: React.ReactElement;
  open?: boolean;
  credits?: number;
  expireTime?: string;
  onUseTemplate?: () => void;
  changeValue?: (name: string, value: string) => void;
  onOpenChange?: (open: boolean) => void;
}

/** 智能体积分弹窗 */
export const CreditsModal: React.FC<CreditsModalProps> = ({
  trigger,
  open: externalOpen,
  credits,
  expireTime: externalExpireTime,
  onOpenChange,
}) => {
  const { t } = useTranslation();

  const [internalOpen, setInternalOpen] = useState(false);
  const [activeKey, setActiveKey] = useState<keyof AgentCreditsRecord>('all');

  // 获取消耗记录
  const { agentCreditsRecord, loading, fetchAgentConsumes } = subscriptionHook.useAgentConsumes();

  const expireTime = externalExpireTime || agentPermissionManager.getUserPermissionDetail()?.orderUser?.expireAt

  const tabs = [
    { key: 'all', label: t('agent_creation.credits_modal.all') },
    { key: 'consumes', label: t('agent_creation.credits_modal.consumption') },
    { key: 'increase', label: t('agent_creation.credits_modal.increase') },
  ];

  // 智能体消耗事件映射
  const AGENT_CONSUMES_EVENT_MAP = {
    miuta_agent_make_clone_short_video: t('agent_creation.credits_modal.short_video'),
    miuta_agent_make_clone_friend_circle: t('agent_creation.credits_modal.moments'),
    miuta_agent_make_clone_article: t('agent_creation.credits_modal.article'),
    miuta_agent_make_clone_picture_text: t('agent_creation.credits_modal.post'),
    miuta_agent_make_clone_pod_cast: t('agent_creation.credits_modal.podcast'),
    system_gift_points: t('agent_creation.credits_modal.system_gift_points'),
    system_gift: t('agent_creation.credits_modal.system_gift_points'),
    miuta_agent_make_clone_pod_cast_zh: t('agent_creation.credits_modal.podcast'),
    miuta_agent_make_clone_pod_cast_en: t('agent_creation.credits_modal.podcast'),

    "miuta_agent_ai_make_sora2_4": t('agent_creation.credits_modal.ai_creation_video'),
    "miuta_agent_ai_make_sora2_8": t('agent_creation.credits_modal.ai_creation_video'),
    "miuta_agent_ai_make_sora2_12": t('agent_creation.credits_modal.ai_creation_video'),
    "miuta_agent_ai_make_sora2_pro_720p_4": t('agent_creation.credits_modal.ai_creation_video'),
    "miuta_agent_ai_make_sora2_pro_720p_8": t('agent_creation.credits_modal.ai_creation_video'),
    "miuta_agent_ai_make_sora2_pro_720p_12": t('agent_creation.credits_modal.ai_creation_video'),
    "miuta_agent_ai_make_sora2_pro_1080p_4": t('agent_creation.credits_modal.ai_creation_video'),
    "miuta_agent_ai_make_sora2_pro_1080p_8": t('agent_creation.credits_modal.ai_creation_video'),
    "miuta_agent_ai_make_sora2_pro_1080p_12": t('agent_creation.credits_modal.ai_creation_video'),
    "miuta_agent_ai_make_kling_2_6_pro_5s": t('agent_creation.credits_modal.ai_creation_video'),
    "miuta_agent_ai_make_kling_2_6_pro_10s": t('agent_creation.credits_modal.ai_creation_video'),

    "miuta_agent_ai_make_nano_banana_2k": t('agent_creation.credits_modal.ai_creation_image'),
    "miuta_agent_ai_make_nano_banana_pro_1k": t('agent_creation.credits_modal.ai_creation_image'),
    "miuta_agent_ai_make_nano_banana_pro_2k": t('agent_creation.credits_modal.ai_creation_image'),
    "miuta_agent_ai_make_nano_banana_pro_4k": t('agent_creation.credits_modal.ai_creation_image'),
    "miuta_agent_ai_make_seedream_2k": t('agent_creation.credits_modal.ai_creation_image'),
    "miuta_agent_ai_make_seedream_4k": t('agent_creation.credits_modal.ai_creation_image'),
    miuta_actor_fast_figure: t('agent_creation.credits_modal.image_to_video'),
    miuta_audio_clone_minimax: t('agent_creation.credits_modal.clone_audio'),
    miuta_audio_tts_1000chr: t('agent_creation.credits_modal.preview_audio'),
    miuta_agent_make_clone_pod_cast_speech_120chr: t('agent_creation.credits_modal.podcast_voiceover'),
    miuta_agent_make_clone_short_speech_120chr: t('agent_creation.credits_modal.short_video_voiceover'),
    miuta_agent_make_clone_short_duration_15s: t('agent_creation.credits_modal.short_video'),

    feature_expire_crontab: t('agent_creation.credits_modal.feature_expired'),
  }

  // 不追加「生成 / 生成失败」后缀的事件：赠送/到期/计费基础值等本身就是完整描述
  const NON_GENERATE_SUFFIX_EVENTS = new Set<string>([
    'system_gift_points',
    'system_gift',
    'feature_expire_crontab',
  ]);

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;

  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  // 处理 open 状态变化
  const handleOpenChange = (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
    if (open) {
      fetchAgentConsumes();
    }
  };

  // 关闭
  const handleClose = () => {
    handleOpenChange(false);
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  // 切换 tab
  const handleTabChange = (key: keyof AgentCreditsRecord) => {
    setActiveKey(key);
  };

  // 当前 tab 的消耗记录
  const currentCreditsRecord = useMemo(() => {
    return agentCreditsRecord[activeKey];
  }, [agentCreditsRecord, activeKey]);

  useEffect(() => {
    return () => {
      setActiveKey('all');
    }
  }, [currentOpen]);

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
      <Dialog.Content
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "w-[640px] max-h-[730px] min-h-[730px] flex-shrink-0",
          "z-50 overflow-hidden"
        )}>
        {/* 标题区域 */}
        <div className="flex max-h-[92px] justify-between items-center px-7 pt-5 pb-2">
          <Dialog.Title
            className='focus:outline-none text-[rgba(24,29,39,1)] text-lg font-semibold flex items-center'
          >
            <span>{t('agent_creation.credits_modal.title')}</span>
          </Dialog.Title>
          <Dialog.Close asChild>
            <button
              className="w-10 h-10 flex justify-center items-center hover:bg-gray-100 rounded-sm"
              tabIndex={-1}
              onClick={handleClose}
            >
              <X className="w-4 h-4 text-[rgba(71,84,103,1)]" />
            </button>
          </Dialog.Close>
        </div>
        {/* 内容 */}
        <div className='flex flex-1 flex-col p-7 pr-0 pt-0 min-h-0'>
          <div className='w-full flex flex-col flex-1 min-h-0 overflow-hidden'>
            <div className='flex items-baseline'>
              <AgentCreditsIcon className="size-5 mr-1.5 flex-shrink-0" />
              <span className='text-[32px] font-semibold text-[rgba(16,24,40,1)]'>
                {formatNumberWithCommas({ num: credits, decimal: 0 })}
              </span>
            </div>
            <div className='flex items-center text-sm text-[rgba(102,112,133,1)] mt-5'>
              {/* <span className=''>
                {t('agent_creation.credits_modal.bonus_points')}
                <span className='pl-1'>{formatNumberWithCommas({ num: 20000, decimal: 0 })}</span>
              </span>
              <div className='border bg-[rgba(233,234,235,1)] h-3 mx-3'></div> */}
              <span className='text-[14px] font-normal text-[rgba(102,112,133,1)]'>
                {t('agent_creation.credits_modal.expire_time')}
                <span className='pl-1'>{expireTime ? dayjs(expireTime).format('YYYY-MM-DD HH:mm:ss') : '-'}</span>
              </span>
            </div>
            <div className='mt-4 pr-7'>
              <SlidingTabs
                tabs={tabs}
                activeKey={activeKey}
                onChange={handleTabChange}
              />
            </div>
            <div className='flex-1 min-h-0 w-full overflow-y-auto mt-5 flex'>
              {loading && <CreditsSkeleton />}
              {!loading && <div className='w-full flex flex-col gap-5 '>
                {currentCreditsRecord.length > 0 ? currentCreditsRecord.map((item, index) => (
                  <div key={index} className='w-full h-10 flex items-center justify-between'>
                    <div className='flex flex-col gap-1.5'>
                      <span className='text-sm text-[rgba(16,24,40,1)]'>
                        {AGENT_CONSUMES_EVENT_MAP[item.event] ? (
                          <div>
                            {AGENT_CONSUMES_EVENT_MAP[item.event]}
                            {!NON_GENERATE_SUFFIX_EVENTS.has(item.event)
                              ? item.type === 2
                                ? `${t('agent_creation.credits_modal.generate_fail')}`
                                : `${t('agent_creation.credits_modal.generate')}`
                              : ''
                            }
                          </div>
                        ) : (
                          <div>
                            {
                              [0, 2].includes(item.type)
                                ? `${t('agent_creation.credits_modal.gift_points')}`
                                : `${t('agent_creation.credits_modal.consumption')}`
                            }
                          </div>
                        )}
                      </span>
                      <span className='text-xs text-[#98a2b3]'>
                        {dayjs(item.createTime * 1000).format('YYYY-MM-DD HH:mm')}
                      </span>
                    </div>
                    <div className={cn('text-[18px] font-medium',
                      [0, 2].includes(item.type) ? 'text-[rgba(117,66,247,1)]' : 'text-[rgba(16,24,40,1)]', 'pr-7')}
                    >
                      {[0, 2].includes(item.type) ? `+ ${item.consumeCount}` : `- ${item.consumeCount}`}
                    </div>
                  </div>
                )) : (
                  <div className='w-full h-full flex items-center justify-center min-h-0 pr-7 '>
                    <div className='w-[94px] h-[110px] flex flex-col items-center'>
                      <AgentCreditsImage />
                      <p className='text-sm text-[rgba(152,162,179,1)] mt-2'>{t('agent_creation.credits_modal.empty_tips')}</p>
                    </div>
                  </div>
                )}
                {currentCreditsRecord.length > 0 && <p className='text-xs w-full text-center text-[rgba(152,162,179,1)]'>
                  {t('agent_creation.credits_modal.no_more')}
                </p>}
              </div>}
            </div>
          </div>
          <p className='text-xs w-full text-center text-[rgba(102,112,133,1)] mt-4 flex-shrink-0'>
            {t('agent_creation.credits_modal.tips')}
          </p>
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
}

const CreditsSkeleton: React.FC = () => {
  return (
    <div className='w-full flex flex-col gap-5 pr-7'>
      {Array.from({ length: 7 }).map((_, index) => (
        <div key={index} className='w-full h-10 flex items-center justify-between animate-pulse'>
          <div className='flex flex-col gap-1.5'>
            <div className='w-16 h-4 bg-[rgba(233,234,235,1)] rounded'></div>
            <div className='w-28 h-3 bg-[rgba(233,234,235,1)] rounded'></div>
          </div>
          <div className='w-16 h-5 bg-[rgba(233,234,235,1)] rounded pr-7'></div>
        </div>
      ))}
    </div>
  );
};